import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import useBreakpoint from '../../lib/helpers/useBreakpoint';
import { BREAKPOINT_MOBILE } from '../../lib/constants';

import styles from './NewStatisticsDevelopment.module.scss';
import { Card, Heading, LinkBase, Text } from '../index';
import useAuthorMode from '../../lib/helpers/useAuthorMode';

const StatisticCard = (props) => {
    const { statistics, editMode } = props;

    return (
        <Card
            className={styles['new-statistics-development__statistic-card']}
            horizontalAlignment="center"
        >
            <div className={styles['new-statistics-development__statistic-card-image-container']}>
                <img
                    className={cx(styles['new-statistics-development__statistic-card-image'], {
                        [`${styles['new-statistics-development__statistic-card-image--edit-mode']}`]:
                            editMode,
                    })}
                    alt=""
                    src={statistics[0].image}
                    loading="lazy"
                />
            </div>
            <div className={styles['new-statistics-development__statistic-card-text-container']}>
                {statistics.map((statistic, index) => (
                    <div
                        className={styles['new-statistics-development__statistic']}
                        key={`statistic-${index}`}
                    >
                        <Heading
                            text={statistic.statistic}
                            headingLevel="h3"
                            overrideStyle="h2"
                            color="purple"
                            className={styles['new-statistics-development__statistic-title']}
                        />
                        <Text
                            content={statistic.subtext}
                            size="large"
                            className={styles['new-statistics-development__statistic-subtext']}
                        />
                        <LinkBase
                            url={statistic.ctaUrl}
                            target={statistic.ctaType}
                            altText={statistic.ctaAltText}
                            className={styles['new-statistics-development__anchor']}
                        >
                            <span
                                className={
                                    styles['new-statistics-development__anchor-text-wrapper']
                                }
                            >
                                <span
                                    className={
                                        styles['new-statistics-development__anchor-text-content']
                                    }
                                >
                                    {statistic.ctaText}
                                    {statistic.ctaType === 'external' ? (
                                        <span
                                            className={
                                                styles[
                                                    'new-statistics-development__external-link-icon'
                                                ]
                                            }
                                        ></span>
                                    ) : null}
                                </span>
                                <span
                                    className={styles['new-statistics-development__arrow-right']}
                                ></span>
                            </span>
                        </LinkBase>
                    </div>
                ))}
            </div>
        </Card>
    );
};

const NewStatisticsDevelopment = (props) => {
    const {
        title,
        fileReferenceOne,
        cardOneStatistic,
        cardOneSubtext,
        cardOneCTAText,
        cardOneCTAAltText,
        cardOneCTAUrl,
        cardOneCTAType,
        fileReferenceTwo,
        cardTwoStatistic,
        cardTwoSubtext,
        cardTwoCTAText,
        cardTwoCTAAltText,
        cardTwoCTAUrl,
        cardTwoCTAType,
        fileReferenceThree,
        cardThreeStatistic,
        cardThreeSubtext,
        cardThreeCTAText,
        cardThreeCTAAltText,
        cardThreeCTAUrl,
        cardThreeCTAType,
        fileReferenceMobile,
    } = props;

    const currentBreakpoint = useBreakpoint();
    const isMobile = currentBreakpoint === BREAKPOINT_MOBILE;

    const currentAuthorMode = useAuthorMode();
    const isEditMode = currentAuthorMode === 'edit';

    const statisticsCardArray = [
        {
            image: isMobile ? fileReferenceMobile : fileReferenceOne,
            statistic: cardOneStatistic,
            subtext: cardOneSubtext,
            ctaUrl: cardOneCTAUrl,
            ctaType: cardOneCTAType,
            ctaText: cardOneCTAText,
            ctaAltText: cardOneCTAAltText,
        },
        {
            image: fileReferenceTwo,
            statistic: cardTwoStatistic,
            subtext: cardTwoSubtext,
            ctaUrl: cardTwoCTAUrl,
            ctaType: cardTwoCTAType,
            ctaText: cardTwoCTAText,
            ctaAltText: cardTwoCTAAltText,
        },
        {
            image: fileReferenceThree,
            statistic: cardThreeStatistic,
            subtext: cardThreeSubtext,
            ctaUrl: cardThreeCTAUrl,
            ctaType: cardThreeCTAType,
            ctaText: cardThreeCTAText,
            ctaAltText: cardThreeCTAAltText,
        },
    ];

    return (
        <div className={styles['new-statistics-development']}>
            <Heading
                text={title}
                headingLevel={isMobile ? 'h3' : 'h2'}
                color="purple"
                alignment="center"
                className={styles['new-statistics-development__title']}
            />
            <div className={styles['new-statistics-development__row']}>
                {!isMobile ? (
                    statisticsCardArray.map((card, index) => (
                        <div
                            className={styles['new-statistics-development__column']}
                            key={`statistics-card-${index}`}
                        >
                            <StatisticCard editMode={isEditMode} statistics={[card]} />
                        </div>
                    ))
                ) : (
                    <div className={styles['new-statistics-development__column']}>
                        <StatisticCard editMode={isEditMode} statistics={statisticsCardArray} />
                    </div>
                )}
            </div>
        </div>
    );
};

NewStatisticsDevelopment.displayName = 'NewStatisticsDevelopment';

NewStatisticsDevelopment.propTypes = {
    title: PropTypes.string.isRequired,
    fileReferenceOne: PropTypes.string.isRequired,
    cardOneStatistic: PropTypes.string.isRequired,
    cardOneSubtext: PropTypes.string.isRequired,
    cardOneCTAText: PropTypes.string.isRequired,
    cardOneCTAAltText: PropTypes.string,
    cardOneCTAUrl: PropTypes.string.isRequired,
    cardOneCTAType: PropTypes.string.isRequired,
    fileReferenceTwo: PropTypes.string.isRequired,
    cardTwoStatistic: PropTypes.string.isRequired,
    cardTwoSubtext: PropTypes.string.isRequired,
    cardTwoCTAText: PropTypes.string.isRequired,
    cardTwoCTAAltText: PropTypes.string,
    cardTwoCTAUrl: PropTypes.string.isRequired,
    cardTwoCTAType: PropTypes.string.isRequired,
    fileReferenceThree: PropTypes.string.isRequired,
    cardThreeStatistic: PropTypes.string.isRequired,
    cardThreeSubtext: PropTypes.string.isRequired,
    cardThreeCTAText: PropTypes.string.isRequired,
    cardThreeCTAUrl: PropTypes.string.isRequired,
    cardThreeCTAType: PropTypes.string.isRequired,
    fileReferenceMobile: PropTypes.string.isRequired,
};

export default NewStatisticsDevelopment;
